import { en, type MessageKey } from "./en";
import { de } from "./de";

/**
 * Translation, without React.
 *
 * Kept pure so it can be tested directly and reused from anywhere — including
 * the callable error mapper, which has no component to sit in.
 */

export type Locale = "en" | "de";
export const LOCALES: readonly Locale[] = ["en", "de"] as const;
export const DEFAULT_LOCALE: Locale = "en";

const CATALOGUES: Record<Locale, Record<string, string>> = { en, de };

export type { MessageKey };

export function isLocale(value: unknown): value is Locale {
  return value === "en" || value === "de";
}

/**
 * Interpolates `{name}` placeholders.
 *
 * Only named placeholders, never positional ones: German and English put the
 * same values in different places in a sentence, and a positional scheme forces
 * a translator to keep an order that their language does not want.
 */
export function interpolate(template: string, params: Record<string, unknown>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    // An unresolved placeholder is left visible rather than blanked. A sentence
    // with `{max}` in it is obviously broken; a sentence reading "That is more
    // than  rows" looks like something a person wrote.
    return value === undefined || value === null ? match : String(value);
  });
}

/** Whether the catalogues define this key at all. */
export function hasKey(key: string): boolean {
  return key in CATALOGUES[DEFAULT_LOCALE];
}

export function translate(
  locale: Locale,
  key: string,
  params: Record<string, unknown> = {}
): string {
  const catalogue = CATALOGUES[locale];
  const template = catalogue[key] ?? CATALOGUES[DEFAULT_LOCALE][key];

  // A missing key shows the key. It is ugly on purpose — the alternative is an
  // empty element that nobody notices until a teacher reports a blank button.
  // In development the context's `t()` throws instead; see I18nContext.
  if (template === undefined) return key;

  return interpolate(template, params);
}

/**
 * Plural selection.
 *
 * `Intl.PluralRules` rather than `count === 1`, because the two languages we
 * ship agree and the next one will not. Both English and German have `one` and
 * `other`; a language with `few` or `many` needs only a wider `forms` object,
 * not a different call site.
 */
export type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>> & { other: string };

export function plural(locale: Locale, count: number, forms: PluralForms): string {
  const category = new Intl.PluralRules(locale === "de" ? "de-DE" : "en-GB").select(count);
  const template = forms[category] ?? forms.other;
  return interpolate(template, { count });
}

/** Every key the catalogues define. Used by the parity test. */
export function keys(): MessageKey[] {
  return Object.keys(en) as MessageKey[];
}

const STORAGE_KEY = "spontar.admin.locale";

/**
 * The stored preference, then the browser's, then English.
 *
 * `localStorage` throws in a locked-down browser rather than returning null, so
 * every access is guarded. A back office that will not load because a privacy
 * setting is strict would be an absurd way to lose an institution.
 */
export function readStoredLocale(): Locale | undefined {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

export function writeStoredLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Preference is not persisted. The session still works.
  }
}

export function detectLocale(): Locale {
  const stored = readStoredLocale();
  if (stored) return stored;

  const languages = typeof navigator === "undefined" ? [] : (navigator.languages ?? []);
  for (const language of languages) {
    const base = language.split("-")[0];
    if (isLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}
